if (!customElements.get('diwali-ank-popup')) {
  customElements.define(
    'diwali-ank-popup',
    class DiwaliAnkPopup extends HTMLElement {
      constructor() {
        super();
        this.dialog = this.querySelector('dialog');
        this.storageKey = `dap-seen:${this.dataset.campaign || 'default'}`;
        this.delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
        this.retries = 0;

        this.onCancel = this.onCancel.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSectionSelect = this.onSectionSelect.bind(this);
        this.onSectionDeselect = this.onSectionDeselect.bind(this);
      }

      connectedCallback() {
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('click', this.onClick);

        if (window.Shopify && Shopify.designMode) {
          // In the theme editor, show only while the section is selected.
          document.addEventListener('shopify:section:select', this.onSectionSelect);
          document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
          return;
        }

        if (this.hasBeenSeen()) return;

        const start = () => {
          this.preloadImages();
          this.timer = setTimeout(() => this.tryOpen(), this.delay);
        };
        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, { once: true });
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        this.unlockScroll();
      }

      hasBeenSeen() {
        try {
          return sessionStorage.getItem(this.storageKey) === '1';
        } catch (e) {
          return false;
        }
      }

      markSeen() {
        try {
          sessionStorage.setItem(this.storageKey, '1');
        } catch (e) {
          // Storage unavailable (private mode): the popup simply shows once per page load.
        }
      }

      // Covers are lazy so they never compete with the page; fetch them just before showing.
      preloadImages() {
        this.dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }

      // Don't interrupt another dialog, drawer, menu or consent banner.
      isPageBusy() {
        if (document.querySelector('dialog[open]:not(.dap)')) return true;
        // Dawn keeps aria-modal on closed drawers, so only count modals that are actually visible.
        const modals = document.querySelectorAll('[aria-modal="true"]');
        for (const modal of modals) {
          if (this.dialog.contains(modal) || !modal.checkVisibility) continue;
          if (modal.checkVisibility({ opacityProperty: true, visibilityProperty: true })) return true;
        }
        const bodyClasses = document.body.classList;
        if (
          bodyClasses.contains('overflow-hidden') ||
          bodyClasses.contains('overflow-hidden-mobile') ||
          bodyClasses.contains('overflow-hidden-tablet') ||
          bodyClasses.contains('overflow-hidden-desktop')
        ) {
          return true;
        }
        const consent = document.getElementById('shopify-pc__banner');
        if (consent && consent.offsetParent !== null) return true;
        return false;
      }

      tryOpen() {
        if (this.hasBeenSeen()) return;
        if (this.isPageBusy()) {
          // Check again shortly; give up after ~1 minute without marking it seen.
          if (this.retries++ < 30) this.timer = setTimeout(() => this.tryOpen(), 2000);
          return;
        }
        this.waitForImages().then(() => {
          if (this.isPageBusy()) return this.tryOpen();
          this.markSeen();
          this.open();
        });
      }

      waitForImages() {
        const images = [...this.dialog.querySelectorAll('img')];
        const loads = images.map((img) =>
          img.complete ? Promise.resolve() : img.decode ? img.decode().catch(() => {}) : Promise.resolve()
        );
        const timeout = new Promise((resolve) => setTimeout(resolve, 2500));
        return Promise.race([Promise.all(loads), timeout]);
      }

      open() {
        if (this.dialog.open) return;
        this.dialog.classList.remove('is-closing');
        this.lockScroll();
        this.dialog.showModal();
      }

      close() {
        if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const finish = () => {
          this.dialog.classList.remove('is-closing');
          this.dialog.close();
          this.unlockScroll();
        };
        if (reduceMotion) return finish();
        this.dialog.classList.add('is-closing');
        setTimeout(finish, 180);
      }

      onCancel(event) {
        // Escape key: run our close animation instead of the instant native close.
        event.preventDefault();
        this.close();
      }

      onClick(event) {
        // The dialog element fills the viewport; only its own surface is "outside" the panel.
        if (event.target === this.dialog || event.target.closest('[data-dap-close]')) {
          this.close();
        }
      }

      lockScroll() {
        if (this.scrollLocked) return;
        const root = document.documentElement;
        const scrollbar = window.innerWidth - root.clientWidth;
        this.previousOverflow = root.style.overflow;
        this.previousPadding = root.style.paddingRight;
        root.style.overflow = 'hidden';
        if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
        this.scrollLocked = true;
      }

      unlockScroll() {
        if (!this.scrollLocked) return;
        const root = document.documentElement;
        root.style.overflow = this.previousOverflow;
        root.style.paddingRight = this.previousPadding;
        this.scrollLocked = false;
      }

      onSectionSelect(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.preloadImages();
        this.open();
      }

      onSectionDeselect(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.close();
      }
    }
  );
}
